import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "columns", "data", "emptyContent", "itemKey", "headerRenderer", "getItemLabel", "a11yLabels", "onRequestSort", "rowHeader", "selectedItems", "onChangeSelectedItems", "sort", "mobileViewBreakpoint", "stickyHeaderOffset", "rowMinHeight"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.array.sort.js";
import "core-js/modules/es.function.name.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/web.dom-collections.for-each.js";
import React from 'react';
import { Checkbox } from 'components/CheckboxOption';
import VisuallyHidden from 'components/VisuallyHidden';
import useUniqueId from 'hooks/useUniqueId';
import useRowSelection, { ALL_SELECTED, SOME_SELECTED } from 'hooks/useRowSelection';
import useGridNavigation from 'hooks/useGridNavigation';
import { getColumnName, getCellContent, getColumnConfigFromChildren } from '../utils';
import { TableContextProvider, TableWrapper, TableColumnHeader, TableHead, TableBody, TableRow, TableBodyCell, TableCheckboxCell, RowSelectButton, InteractiveTableContent } from '../shared';
import useClickRowToSelect from './useClickRowToSelect';
import { defaultHeaderRenderer, DefaultEmptyContent, useDefaultA11yLabels } from '../Table';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function SelectionTable(_ref) {
  var children = _ref.children,
    _ref$columns = _ref.columns,
    columnsProp = _ref$columns === void 0 ? [] : _ref$columns,
    data = _ref.data,
    _ref$emptyContent = _ref.emptyContent,
    emptyContent = _ref$emptyContent === void 0 ? /*#__PURE__*/_jsx(DefaultEmptyContent, {}) : _ref$emptyContent,
    _ref$itemKey = _ref.itemKey,
    itemKey = _ref$itemKey === void 0 ? 'id' : _ref$itemKey,
    _ref$headerRenderer = _ref.headerRenderer,
    headerRenderer = _ref$headerRenderer === void 0 ? defaultHeaderRenderer : _ref$headerRenderer,
    _ref$getItemLabel = _ref.getItemLabel,
    getItemLabel = _ref$getItemLabel === void 0 ? function (_item, index) {
      return "Item ".concat(index);
    } : _ref$getItemLabel,
    _ref$a11yLabels = _ref.a11yLabels,
    a11yLabels = _ref$a11yLabels === void 0 ? undefined : _ref$a11yLabels,
    _ref$onRequestSort = _ref.onRequestSort,
    onRequestSort = _ref$onRequestSort === void 0 ? undefined : _ref$onRequestSort,
    rowHeaderProp = _ref.rowHeader,
    selectedItems = _ref.selectedItems,
    onChangeSelectedItems = _ref.onChangeSelectedItems,
    _ref$sort = _ref.sort,
    sort = _ref$sort === void 0 ? undefined : _ref$sort,
    _ref$mobileViewBreakp = _ref.mobileViewBreakpoint,
    mobileViewBreakpoint = _ref$mobileViewBreakp === void 0 ? 'xs' : _ref$mobileViewBreakp,
    _ref$stickyHeaderOffs = _ref.stickyHeaderOffset,
    stickyHeaderOffset = _ref$stickyHeaderOffs === void 0 ? 0 : _ref$stickyHeaderOffs,
    _ref$rowMinHeight = _ref.rowMinHeight,
    rowMinHeight = _ref$rowMinHeight === void 0 ? 45 : _ref$rowMinHeight,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var defaultA11yLabels = useDefaultA11yLabels();
  var resolvedA11yLabels = _objectSpread(_objectSpread({}, defaultA11yLabels), a11yLabels);
  var columns = children ? getColumnConfigFromChildren(children) : columnsProp;
  var hasData = data && data.length > 0;
  var rowHeader = rowHeaderProp || getColumnName(columns[0]);
  function getItemId(item) {
    return item[itemKey];
  }
  var rowIds = hasData ? data.map(function (item) {
    return getItemId(item);
  }) : [];
  var getInitialHighlightedRow = function getInitialHighlightedRow() {
    return selectedItems.length ? selectedItems[0] : hasData ? getItemId(data[0]) : null;
  };
  var _useGridNavigation = useGridNavigation({
      rowIds: rowIds,
      getInitialHighlightedRow: getInitialHighlightedRow
    }),
    highlightedRowIndex = _useGridNavigation.highlightedRowIndex,
    getIsRowHighlighted = _useGridNavigation.getIsRowHighlighted,
    getRowProps = _useGridNavigation.getRowProps,
    getCellProps = _useGridNavigation.getCellProps,
    handleGridNavigationKeys = _useGridNavigation.handleGridNavigationKeys;
  var _useRowSelection = useRowSelection({
      rowIds: rowIds,
      highlightedRowIndex: highlightedRowIndex,
      selectedItemIds: selectedItems,
      onChange: onChangeSelectedItems
    }),
    selectAllState = _useRowSelection.selectAllState,
    getIsRowSelected = _useRowSelection.getIsRowSelected,
    getSelectRowHandler = _useRowSelection.getSelectRowHandler,
    getSelectSingleRowHandler = _useRowSelection.getSelectSingleRowHandler,
    toggleSelectAll = _useRowSelection.toggleSelectAll,
    handleGridSelectionKeys = _useRowSelection.handleGridSelectionKeys;
  var _useClickRowToSelect = useClickRowToSelect(),
    getRowSelectButtonRef = _useClickRowToSelect.getRowSelectButtonRef,
    getRowClickHandler = _useClickRowToSelect.getRowClickHandler;
  var navigationHintId = useUniqueId();
  return /*#__PURE__*/_jsxs(TableContextProvider, _objectSpread(_objectSpread({
    mobileViewBreakpoint: mobileViewBreakpoint,
    stickyHeaderOffset: stickyHeaderOffset,
    rowMinHeight: rowMinHeight
  }, otherProps), {}, {
    children: [/*#__PURE__*/_jsxs(TableWrapper, {
      role: "grid",
      "aria-label": resolvedA11yLabels.label,
      "aria-multiselectable": "true",
      "aria-describedby": navigationHintId,
      onKeyDown: function onKeyDown(event) {
        handleGridNavigationKeys(event);
        handleGridSelectionKeys(event);
      },
      children: [/*#__PURE__*/_jsx(TableHead, {
        children: /*#__PURE__*/_jsxs(TableRow, {
          children: [/*#__PURE__*/_jsx(TableCheckboxCell, {
            isHeader: true,
            children: /*#__PURE__*/_jsx(Checkbox, {
              size: "small",
              checked: selectAllState === ALL_SELECTED,
              indeterminate: selectAllState === SOME_SELECTED,
              onChange: toggleSelectAll,
              "aria-label": resolvedA11yLabels.selectAllRows
            })
          }), columns.map(function (column) {
            return /*#__PURE__*/_jsx(TableColumnHeader, {
              column: column,
              sort: sort,
              a11yLabels: resolvedA11yLabels,
              onRequestSort: onRequestSort,
              children: headerRenderer(column)
            }, getColumnName(column));
          })]
        })
      }), /*#__PURE__*/_jsx(TableBody, {
        children: hasData && data.map(function (item, index) {
          var rowId = getItemId(item);
          var itemLabel = getItemLabel(item, index + 1);
          var isSelected = getIsRowSelected(rowId);
          var isRowHighlighted = getIsRowHighlighted(rowId);
          return /*#__PURE__*/_jsxs(TableRow, _objectSpread(_objectSpread({
            selectable: true
          }, getRowProps(rowId)), {}, {
            onClick: getRowClickHandler(rowId),
            isHighlighted: isRowHighlighted,
            isSelected: isSelected,
            children: [/*#__PURE__*/_jsxs(TableCheckboxCell, _objectSpread(_objectSpread({}, getCellProps(rowId, 0)), {}, {
              tabIndex: -1,
              children: [/*#__PURE__*/_jsx(RowSelectButton, {
                ref: getRowSelectButtonRef(rowId),
                onClick: getSelectSingleRowHandler(rowId),
                isActive: isSelected && selectedItems.length === 1,
                tabIndex: isRowHighlighted ? 0 : -1,
                children: resolvedA11yLabels.selectSingleRow(itemLabel)
              }), /*#__PURE__*/_jsx(InteractiveTableContent, {
                children: /*#__PURE__*/_jsx(Checkbox, {
                  size: "small",
                  checked: isSelected,
                  onChange: getSelectRowHandler(rowId),
                  "aria-label": itemLabel
                })
              })]
            })), columns.map(function (column, colIndex) {
              var cellRenderer = column.cellRenderer,
                hideBelowBreakpoint = column.hideBelowBreakpoint,
                name = column.name,
                title = column.title;
              var columnName = getColumnName(column);
              var isHeader = [name, title].includes(rowHeader);
              return /*#__PURE__*/_jsx(TableBodyCell, _objectSpread(_objectSpread({}, getCellProps(rowId, colIndex + 1)), {}, {
                tabIndex: -1,
                title: title,
                isHeader: isHeader,
                hideBelowBreakpoint: hideBelowBreakpoint,
                children: getCellContent(item, cellRenderer || columnName)
              }), columnName);
            })]
          }), rowId);
        })
      })]
    }), /*#__PURE__*/_jsx(VisuallyHidden, {
      id: navigationHintId,
      children: resolvedA11yLabels.navigationHint
    }), !hasData && emptyContent]
  }));
}
export { InteractiveTableContent };
export default SelectionTable;